// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe, FileText, Film, Plug, Search, ChevronLeft, ChevronDown, ChevronUp,
  ExternalLink, X, AlertTriangle, Loader2, ArrowRight, Sparkles, Plus, Lock,
} from 'lucide-react';
import {
  fetchKnowledgeBase, type KnowledgeBaseSummary,
  fetchSourceDocuments, type KbDocument,
} from '../services/onboardingApi';
import { getMyBusiness } from '../services/api';
import { resolveProvider } from '../components/onboarding/providerCatalog';
import OrbitDock from '../components/onboarding/OrbitDock';
import { BRAND } from '../constants/brand';

/**
 * KnowledgeBrowser — /knowledge
 *
 * The single answer to "where does my chatbot get its knowledge?" for the
 * customer. Four content layers, each visually distinct:
 *
 *   1. Website pages we've scraped
 *   2. Documents (uploaded or extracted by Orbit)
 *   3. Videos (YouTube / Vimeo with transcripts) — empty today
 *   4. Connected apps — opt-in to feed each app's data into the chatbot's KB
 *
 * Browse-only for the page itself (chatbot customization lives elsewhere).
 * The "use this app as a source" action is the one place the customer takes
 * action here — it's how they let Orbit pull from their already-connected
 * apps into the RAG store.
 *
 * Phase A (this version): UI complete. Static sources render real
 * kb_documents data. App opt-in opens an Orbit-chat invite (the chat UX
 * does the actual setup with the user, conversationally). Phase B will
 * replace the chat invite with a direct sync trigger + per-app config UI
 * once the per-app pull adapters are built.
 */

// Apps Orbit will support as KB sources, in priority order. Empty if not
// connected. The page renders ONLY apps the customer has actually linked
// (filtered against `connections`) — no fake placeholders for apps they
// don't have.
const KB_CAPABLE_APPS: Array<{ slug: string; pitch: string }> = [
  { slug: 'gmail',          pitch: 'Pull selected labels into the bot so it can reference customer-support threads and your tone.' },
  { slug: 'google_drive',   pitch: 'Sync a folder of docs — your Drive becomes searchable knowledge.' },
  { slug: 'onedrive',       pitch: 'Sync a OneDrive folder of docs — searchable by the chatbot.' },
  { slug: 'dropbox',        pitch: 'Sync a Dropbox folder of files into searchable knowledge.' },
  { slug: 'notion',         pitch: 'Connect a Notion database (help docs, FAQs, runbooks) as a live KB.' },
  { slug: 'slack',          pitch: 'Index public channel discussions as conversational knowledge.' },
  { slug: 'google_calendar',pitch: 'Surface upcoming events when customers ask about availability or schedule.' },
];

function formatRelative(iso: string | null): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const ms = Date.now() - t;
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

const KnowledgeBrowser: React.FC = () => {
  const navigate = useNavigate();
  const clientId = localStorage.getItem('client_id') || '';

  const [data, setData] = useState<KnowledgeBaseSummary | null>(null);
  const [connections, setConnections] = useState<Array<{ id: string; provider: string; status: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  // Per-source expansion (used for the website domain card's "View all" link)
  const [expandedSources, setExpandedSources] = useState<Record<string, KbDocument[] | 'loading' | 'error'>>({});

  // Modal state for the "Use as source" action
  const [appModal, setAppModal] = useState<null | { slug: string; label: string; pitch: string }>(null);

  // ─── Fetch ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setError(null);
        const [kb, biz] = await Promise.all([
          fetchKnowledgeBase({ clientId, recentLimit: 12 }),
          getMyBusiness(clientId).catch(() => null),
        ]);
        if (cancelled) return;
        setData(kb);
        setConnections((biz as any)?.connections || []);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load knowledge base');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [clientId, refreshKey]);

  useEffect(() => {
    const onUpdate = () => setRefreshKey(k => k + 1);
    window.addEventListener('orbit:tile-update', onUpdate);
    return () => window.removeEventListener('orbit:tile-update', onUpdate);
  }, []);

  // ─── Derived: split sources by category ──────────────────────────
  const websiteSources = useMemo(() => (data?.by_source || []).filter(s => s.type === 'website'), [data]);
  const documentSources = useMemo(() => (data?.by_source || []).filter(s => s.type === 'document'), [data]);
  const videoSources = useMemo(() => (data?.by_source || []).filter(s => s.type === 'video'), [data]);

  // Connected apps that COULD be a KB source (we have a pitch + they're linked + active).
  // Future: detect already-syncing apps from kb_sources table (Phase B) and
  // show them with active status / sync-now / settings affordances instead.
  const connectedAppSlugs = useMemo(
    () => new Set(connections.filter(c => c.status === 'connected').map(c => c.provider)),
    [connections],
  );
  const offerableApps = useMemo(
    () => KB_CAPABLE_APPS.filter(a => connectedAppSlugs.has(a.slug)),
    [connectedAppSlugs],
  );

  // Apps NOT yet connected — surfaced as "Connect to add as source"
  const suggestableApps = useMemo(
    () => KB_CAPABLE_APPS.filter(a => !connectedAppSlugs.has(a.slug)).slice(0, 4),
    [connectedAppSlugs],
  );

  const sourceIdFor = (s: { type: string; label: string }): string =>
    s.type === 'website' ? `web:${s.label}` :
    s.type === 'document' ? 'uploaded' : `app:${s.label}`;

  const toggleExpand = useCallback(async (s: { type: string; label: string }) => {
    const id = sourceIdFor(s);
    if (expandedSources[id] && expandedSources[id] !== 'loading' && expandedSources[id] !== 'error') {
      setExpandedSources(prev => { const next = { ...prev }; delete next[id]; return next; });
      return;
    }
    setExpandedSources(prev => ({ ...prev, [id]: 'loading' }));
    try {
      const res = await fetchSourceDocuments({ sourceId: id, clientId, limit: 50 });
      setExpandedSources(prev => ({ ...prev, [id]: res.documents }));
    } catch {
      setExpandedSources(prev => ({ ...prev, [id]: 'error' }));
    }
  }, [clientId, expandedSources]);

  // Search filter — applies to source labels + recent doc titles
  const matchesSearch = useCallback((label: string): boolean => {
    if (!search.trim()) return true;
    return label.toLowerCase().includes(search.trim().toLowerCase());
  }, [search]);

  if (!clientId) {
    return (
      <div className="p-12 text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No client found</h2>
        <p className="text-sm text-gray-500">Sign in to view your knowledge base.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Page header — quiet, contextual, links back to My Business */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-5">
          <button
            type="button"
            onClick={() => navigate('/my-business')}
            className="inline-flex items-center gap-1.5 text-[12.5px] text-gray-600 hover:text-gray-900 transition-colors mb-2"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            My Business
          </button>
          <h1 className="text-[22px] font-bold text-gray-900 leading-tight tracking-tight">Knowledge Base</h1>
          <p className="text-[13px] text-gray-600 mt-1 max-w-2xl leading-relaxed">
            Where your chatbot's answers come from. Everything below is indexed and searchable when your bot answers.
            Add to it by letting your connected apps feed in their data.
          </p>

          {/* Hero summary — one sentence aggregate */}
          {data && data.totals.all > 0 && (
            <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium"
              style={{
                background: 'rgba(64,128,242,0.08)',
                color: BRAND.blueDeep,
                border: '1px solid rgba(64,128,242,0.15)',
              }}
            >
              <Sparkles className="w-3.5 h-3.5" />
              Your chatbot has access to{' '}
              <strong className="font-semibold">{data.totals.all}</strong>
              {' '}{data.totals.all === 1 ? 'item' : 'items'} across{' '}
              <strong className="font-semibold">
                {websiteSources.length + documentSources.length + videoSources.length + offerableApps.length}
              </strong>{' '}
              source{(websiteSources.length + documentSources.length + videoSources.length + offerableApps.length) === 1 ? '' : 's'}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-8">
        {/* Search */}
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-3">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search across your knowledge base…"
            className="flex-1 bg-transparent text-[13.5px] outline-none placeholder:text-gray-400"
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-700">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-[13px] text-red-700 inline-flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        ) : loading ? (
          <div className="space-y-4">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-white rounded-xl border border-gray-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {/* ─────── Section 1: Website pages ─────── */}
            <CategorySection
              icon={Globe}
              tint="rgba(64,128,242,0.10)"
              iconColor={BRAND.blueDeep}
              title="Website pages"
              description="Pages we scraped from your site."
              count={data?.totals.pages || 0}
              countLabel="pages"
              empty="Orbit hasn't crawled a website yet. Re-run onboarding with your URL or ask Orbit to research it."
            >
              {websiteSources.filter(s => matchesSearch(s.label)).map(s => {
                const id = sourceIdFor(s);
                const expanded = expandedSources[id];
                return (
                  <SourceCard
                    key={id}
                    primaryLabel={s.label}
                    secondaryLabel={`${s.count} pages · last crawled ${formatRelative(s.last_added)}`}
                    accent={BRAND.blueDeep}
                    expanded={!!expanded && expanded !== 'loading' && expanded !== 'error'}
                    onToggle={() => toggleExpand(s)}
                    expandedContent={
                      expanded === 'loading' ? (
                        <DocListLoading />
                      ) : expanded === 'error' ? (
                        <div className="text-[12px] text-red-600">Failed to load pages</div>
                      ) : Array.isArray(expanded) ? (
                        <DocList docs={expanded} />
                      ) : null
                    }
                  />
                );
              })}
            </CategorySection>

            {/* ─────── Section 2: Documents ─────── */}
            <CategorySection
              icon={FileText}
              tint="rgba(168,85,247,0.10)"
              iconColor="#7C3AED"
              title="Documents"
              description="Files you uploaded or Orbit extracted during onboarding."
              count={data?.totals.documents || 0}
              countLabel="files"
              empty="No documents yet. Upload PDFs, decks, or briefs through Orbit chat."
            >
              {documentSources.filter(s => matchesSearch(s.label)).map(s => {
                const id = sourceIdFor(s);
                const expanded = expandedSources[id];
                return (
                  <SourceCard
                    key={id}
                    primaryLabel="Uploaded files"
                    secondaryLabel={`${s.count} ${s.count === 1 ? 'document' : 'documents'} · last updated ${formatRelative(s.last_added)}`}
                    accent="#7C3AED"
                    expanded={!!expanded && expanded !== 'loading' && expanded !== 'error'}
                    onToggle={() => toggleExpand(s)}
                    expandedContent={
                      expanded === 'loading' ? (
                        <DocListLoading />
                      ) : expanded === 'error' ? (
                        <div className="text-[12px] text-red-600">Failed to load documents</div>
                      ) : Array.isArray(expanded) ? (
                        <DocList docs={expanded} />
                      ) : null
                    }
                  />
                );
              })}
            </CategorySection>

            {/* ─────── Section 3: Videos ─────── */}
            <CategorySection
              icon={Film}
              tint="rgba(220,38,38,0.08)"
              iconColor="#DC2626"
              title="Videos"
              description="YouTube or Vimeo videos with transcripts indexed."
              count={data?.totals.videos || 0}
              countLabel="videos"
              empty="None yet — ask Orbit to add a video URL."
            >
              {/* Phase B: video transcripts as kb_documents with source_type='video' */}
            </CategorySection>

            {/* ─────── Section 4: Connected apps as KB sources ───────
                The action layer of the page. Customer's already-linked
                apps with a clear "use this for the chatbot" gesture. */}
            <ConnectedAppsKbSection
              offerable={offerableApps}
              suggestable={suggestableApps}
              onUseApp={(app) => setAppModal(app)}
              onConnectMore={() => navigate('/my-business')}
              hasAnyConnected={connectedAppSlugs.size > 0}
            />
          </>
        )}
      </div>

      {/* Use-app modal — Phase A: dispatch an Orbit chat invite that walks
          the user through KB sync setup conversationally. Phase B will
          replace this with a direct sync-config UI + per-app pull. */}
      <AppKbSyncModal
        app={appModal}
        onClose={() => setAppModal(null)}
        onConfirm={(slug, label) => {
          const prompt = `I want to use my ${label} data as a knowledge source for my chatbot. Walk me through what would get indexed and how to set it up.`;
          try {
            window.dispatchEvent(new CustomEvent('orbit:open-with-prompt', { detail: { text: prompt } }));
          } catch { /* noop */ }
          setAppModal(null);
        }}
      />

      <OrbitDock lane="dashboard" surface="knowledge" />
    </div>
  );
};

// ============================================================================
// Subcomponents
// ============================================================================

const CategorySection: React.FC<{
  icon: React.ElementType;
  tint: string;
  iconColor: string;
  title: string;
  description: string;
  count: number;
  countLabel: string;
  empty: string;
  children: React.ReactNode;
}> = ({ icon: Icon, tint, iconColor, title, description, count, countLabel, empty, children }) => {
  // Empty if no children rendered. Use React.Children to detect "really empty".
  const hasContent = React.Children.toArray(children).some(c => !!c);
  return (
    <section>
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: tint, color: iconColor }}
        >
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <h2 className="text-[15px] font-bold text-gray-900 tracking-tight">{title}</h2>
            <span className="text-[11.5px] text-gray-500">
              {count > 0 ? `${count} ${countLabel}` : 'None yet'}
            </span>
          </div>
          <p className="text-[12px] text-gray-500 mt-0.5">{description}</p>
        </div>
      </div>

      {hasContent ? (
        <div className="space-y-2">{children}</div>
      ) : (
        <div className="bg-white border border-dashed border-gray-200 rounded-xl px-5 py-6 text-[12px] text-gray-500 italic">
          {empty}
        </div>
      )}
    </section>
  );
};

const SourceCard: React.FC<{
  primaryLabel: string;
  secondaryLabel: string;
  accent: string;
  expanded: boolean;
  onToggle: () => void;
  expandedContent: React.ReactNode;
}> = ({ primaryLabel, secondaryLabel, accent, expanded, onToggle, expandedContent }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-semibold text-gray-900 truncate">{primaryLabel}</div>
          <div className="text-[11.5px] text-gray-500 mt-0.5">{secondaryLabel}</div>
        </div>
        <div className="flex items-center gap-1.5 text-[11.5px] font-semibold" style={{ color: accent }}>
          {expanded ? 'Hide' : 'View'}
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </div>
      </button>
      <AnimatePresence>
        {expanded && expandedContent && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden border-t border-gray-100"
          >
            <div className="px-4 py-3 bg-gray-50/50">{expandedContent}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const DocListLoading: React.FC = () => (
  <div className="text-[12px] text-gray-500 inline-flex items-center gap-1.5 py-2">
    <Loader2 className="w-3 h-3 animate-spin" />
    Loading items…
  </div>
);

const DocList: React.FC<{ docs: KbDocument[] }> = ({ docs }) => {
  if (docs.length === 0) {
    return <div className="text-[12px] text-gray-500 italic py-2">No items.</div>;
  }
  return (
    <ol className="space-y-1">
      {docs.map(doc => (
        <li key={doc.id} className="flex items-start gap-2 px-2 py-1.5 rounded-md hover:bg-white transition-colors">
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-medium text-gray-800 truncate">{doc.title}</div>
            {doc.source_url && doc.source_url.startsWith('http') && (
              <a
                href={doc.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-gray-500 hover:text-gray-800 transition-colors inline-flex items-center gap-1"
              >
                <span className="truncate max-w-[400px]">{doc.source_url}</span>
                <ExternalLink className="w-2.5 h-2.5 opacity-70 shrink-0" />
              </a>
            )}
          </div>
          <span className="text-[10.5px] text-gray-400 shrink-0">{formatRelative(doc.created_at)}</span>
        </li>
      ))}
    </ol>
  );
};

// ─── Connected apps (KB source opt-in) ──────────────────────────

const ConnectedAppsKbSection: React.FC<{
  offerable: typeof KB_CAPABLE_APPS;
  suggestable: typeof KB_CAPABLE_APPS;
  onUseApp: (app: { slug: string; label: string; pitch: string }) => void;
  onConnectMore: () => void;
  hasAnyConnected: boolean;
}> = ({ offerable, suggestable, onUseApp, onConnectMore, hasAnyConnected }) => {
  return (
    <section>
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{
            background: `linear-gradient(135deg, rgba(64,128,242,0.12) 0%, rgba(255,234,0,0.10) 100%)`,
            color: BRAND.blueDeep,
          }}
        >
          <Plug className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <h2 className="text-[15px] font-bold text-gray-900 tracking-tight">Your connected apps</h2>
            <span className="text-[11.5px] text-gray-500">
              {offerable.length === 0 ? 'None offered yet' : `${offerable.length} can feed your chatbot`}
            </span>
          </div>
          <p className="text-[12px] text-gray-500 mt-0.5">
            Apps you've already linked. Let any of them feed your bot's knowledge — opt in per app, only the data you choose.
          </p>
        </div>
      </div>

      {/* Offerable apps — already connected, can be used as KB source */}
      {offerable.length > 0 ? (
        <div className="space-y-2">
          {offerable.map(app => {
            const { provider } = resolveProvider(app.slug);
            return (
              <div
                key={app.slug}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <div className="flex items-start gap-3 px-4 py-3.5">
                  {/* Brand-colored logo block */}
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center font-bold text-white shrink-0"
                    style={{
                      background: provider.color,
                      color: provider.textColor || 'white',
                      fontSize: 16,
                      letterSpacing: '-0.5px',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), 0 1px 2px rgba(0,0,0,0.08)',
                    }}
                    aria-hidden
                  >
                    {provider.initial}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[14px] font-bold text-gray-900">{provider.label}</span>
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                        style={{ background: 'rgba(34,197,94,0.10)', color: '#15803D' }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        Connected
                      </span>
                    </div>
                    <div className="text-[12px] text-gray-600 mt-1 leading-relaxed">{app.pitch}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onUseApp({ slug: app.slug, label: provider.label, pitch: app.pitch })}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold transition-all shrink-0 mt-1 whitespace-nowrap"
                    style={{
                      background: BRAND.blue,
                      color: 'white',
                      boxShadow: '0 2px 6px rgba(64,128,242,0.22)',
                    }}
                  >
                    Use as source
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-dashed border-gray-200 rounded-xl px-5 py-8 text-center">
          {hasAnyConnected ? (
            <>
              <div className="text-[12.5px] text-gray-700 mb-1">
                You have apps connected, but none of them are wired up as KB sources yet.
              </div>
              <div className="text-[11.5px] text-gray-500">
                Apps that can feed your chatbot will appear here as we add support for them.
              </div>
            </>
          ) : (
            <>
              <div className="text-[12.5px] text-gray-700 mb-2">
                You haven't connected any apps that can feed your chatbot.
              </div>
              <button
                type="button"
                onClick={onConnectMore}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-semibold whitespace-nowrap"
                style={{ background: BRAND.blue, color: 'white' }}
              >
                <Plus className="w-3.5 h-3.5" />
                Connect an app
              </button>
            </>
          )}
        </div>
      )}

      {/* Suggestable — apps NOT yet connected but commonly used as KB sources */}
      {suggestable.length > 0 && (
        <div className="mt-5 bg-gray-50 rounded-xl border border-gray-100 px-4 py-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-gray-500 mb-1">
                Want more sources?
              </div>
              <div className="text-[12px] text-gray-600">
                Connect any of these and they'll show up above:
                {' '}
                {suggestable.map((a, i) => {
                  const { provider } = resolveProvider(a.slug);
                  return (
                    <span key={a.slug} className="inline-flex items-center gap-1 mr-2">
                      <span
                        className="w-3.5 h-3.5 rounded inline-flex items-center justify-center text-[8px] font-bold text-white"
                        style={{ background: provider.color }}
                        aria-hidden
                      >
                        {provider.initial}
                      </span>
                      <span className="font-medium text-gray-700">{provider.label}</span>
                      {i < suggestable.length - 1 && <span className="text-gray-400">·</span>}
                    </span>
                  );
                })}
              </div>
            </div>
            <button
              type="button"
              onClick={onConnectMore}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11.5px] font-semibold text-gray-700 hover:bg-white border border-gray-200 transition-colors shrink-0 whitespace-nowrap"
            >
              <Plus className="w-3 h-3" />
              Connect more
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

// ─── Use-as-source modal ─────────────────────────────────────

const AppKbSyncModal: React.FC<{
  app: { slug: string; label: string; pitch: string } | null;
  onClose: () => void;
  onConfirm: (slug: string, label: string) => void;
}> = ({ app, onClose, onConfirm }) => {
  if (!app) return null;
  const { provider } = resolveProvider(app.slug);
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[61] flex items-center justify-center p-4 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 4 }}
          transition={{ duration: 0.18 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden pointer-events-auto"
        >
          {/* Header with brand-colored band */}
          <div
            className="px-5 py-5 flex items-start gap-3 border-b border-gray-100"
            style={{
              background: `linear-gradient(135deg, ${provider.color}10 0%, transparent 100%)`,
            }}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-white shrink-0"
              style={{
                background: provider.color, fontSize: 18, letterSpacing: '-0.5px',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), 0 2px 6px rgba(0,0,0,0.08)',
              }}
              aria-hidden
            >
              {provider.initial}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-[16px] font-bold text-gray-900 leading-tight">
                Use {provider.label} as a chatbot source
              </h3>
              <p className="text-[12.5px] text-gray-600 mt-1 leading-relaxed">{app.pitch}</p>
            </div>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 rounded-md">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="px-5 py-4 space-y-3">
            <div className="text-[12.5px] text-gray-700 leading-relaxed">
              When you set this up, Orbit will start pulling selected data from {provider.label} and adding it to your chatbot's knowledge base. The chatbot will then be able to answer questions using that data.
            </div>

            {/* "What does Orbit need from you" — one-line setup ask */}
            <div className="rounded-lg p-3" style={{ background: 'rgba(64,128,242,0.05)', border: '1px solid rgba(64,128,242,0.12)' }}>
              <div className="text-[11.5px] font-semibold text-gray-700 mb-1">Next step</div>
              <div className="text-[12px] text-gray-600 leading-relaxed">
                Orbit will ask which {app.slug === 'gmail' ? 'labels' : app.slug.includes('drive') || app.slug.includes('dropbox') || app.slug.includes('onedrive') ? 'folders' : 'spaces'} you want indexed and confirm before pulling anything.
              </div>
            </div>

            <div className="rounded-lg p-3 inline-flex items-start gap-2" style={{ background: 'rgba(17,24,39,0.03)' }}>
              <Lock className="w-3.5 h-3.5 text-gray-500 mt-0.5 shrink-0" />
              <div className="text-[11.5px] text-gray-600 leading-relaxed">
                Read-only by default. You can stop or revoke at any time from Connected Apps.
              </div>
            </div>
          </div>

          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-md text-[12px] text-gray-600 hover:bg-gray-50"
            >
              Not now
            </button>
            <button
              type="button"
              onClick={() => onConfirm(app.slug, provider.label)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12.5px] font-semibold transition-colors whitespace-nowrap"
              style={{ background: BRAND.blue, color: 'white', boxShadow: '0 2px 6px rgba(64,128,242,0.22)' }}
            >
              Set this up with Orbit
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default KnowledgeBrowser;
